/**
 * Food System API Contract Schemas
 *
 * Defines Zod schemas for food-related API requests and responses.
 * Used for validation in both backend (NestJS) and frontend (React forms).
 */

import { z } from 'zod';

// ============================================
// ENUMS
// ============================================

export const DiscountTypeEnum = z.enum(['PERCENTAGE', 'FLAT']);
export type DiscountType = z.infer<typeof DiscountTypeEnum>;

export const FoodOfferTypeEnum = z.enum(['CART_REWARD', 'COMBO']);
export type FoodOfferType = z.infer<typeof FoodOfferTypeEnum>;

// ============================================
// FOOD CATEGORY SCHEMAS (Admin-only)
// ============================================

/**
 * Create food category request schema (Admin)
 *
 * Validation rules:
 * - name: min 2, max 100 characters
 * - description: max 500 characters
 * - image: valid URL
 * - displayOrder: non-negative integer
 */
export const CreateFoodCategorySchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  description: z.string().max(500).optional(),
  image: z.string().url('Invalid image URL').optional(),
  displayOrder: z.number().int().min(0).optional(),
});
export type CreateFoodCategoryRequest = z.infer<
  typeof CreateFoodCategorySchema
>;

export const UpdateFoodCategorySchema = CreateFoodCategorySchema.partial();
export type UpdateFoodCategoryRequest = z.infer<
  typeof UpdateFoodCategorySchema
>;

export const ReorderFoodCategoriesSchema = z.object({
  categoryIds: z.array(z.string().uuid()),
});
export type ReorderFoodCategoriesRequest = z.infer<
  typeof ReorderFoodCategoriesSchema
>;

// ============================================
// FOOD ITEM SCHEMAS (Vendor)
// ============================================

/**
 * Create food item request schema (Vendor)
 *
 * Validation rules:
 * - categoryId: valid UUID
 * - name: min 2, max 100 characters
 * - description: max 500 characters
 * - image: valid URL (from R2)
 * - price: non-negative number with max 2 decimal places
 * - currency: 3-letter code (default INR)
 * - isVegetarian/isVegan: boolean flags
 * - allergens: array of strings
 * - spiceLevel: 0-3 (0=none, 1=mild, 2=medium, 3=hot)
 * - preparationTime: minutes (non-negative integer)
 * - dailyLimit: positive integer (null = unlimited)
 * - displayOrder: non-negative integer
 * - isAvailable: boolean (default true)
 */
export const CreateFoodItemSchema = z.object({
  categoryId: z.string().uuid('Invalid category ID'),
  name: z.string().min(2).max(100),
  description: z.string().max(500).optional(),
  image: z.string().url('Invalid image URL').optional(),
  price: z.number({ invalid_type_error: 'Price must be a number' }).min(0),
  currency: z.string().length(3).optional().default('INR'),

  // Dietary information
  isVegetarian: z.boolean().optional().default(false),
  isVegan: z.boolean().optional().default(false),
  allergens: z.array(z.string()).optional().default([]),
  spiceLevel: z
    .number()
    .int()
    .min(0, 'Spice level must be 0-3')
    .max(3, 'Spice level must be 0-3')
    .optional()
    .default(0),

  // Operational
  preparationTime: z
    .number()
    .int()
    .min(0, 'Preparation time must be non-negative')
    .optional(),
  dailyLimit: z
    .number()
    .int()
    .min(1, 'Daily limit must be at least 1')
    .optional(),
  displayOrder: z.number().int().min(0).optional(),
  isAvailable: z.boolean().optional().default(true),
});
export type CreateFoodItemRequest = z.infer<typeof CreateFoodItemSchema>;

export const UpdateFoodItemSchema = CreateFoodItemSchema.partial();
export type UpdateFoodItemRequest = z.infer<typeof UpdateFoodItemSchema>;

/**
 * Set item-level discount schema
 *
 * Validation rules:
 * - discountType: PERCENTAGE or FLAT
 * - discountValue: non-negative number, <= 100 if PERCENTAGE
 * - discountStartDate: ISO datetime string
 * - discountEndDate: ISO datetime string
 */
export const SetFoodItemDiscountSchema = z.object({
  discountType: DiscountTypeEnum,
  discountValue: z.number().min(0, 'Discount value must be non-negative'),
  discountStartDate: z.string().datetime().optional(),
  discountEndDate: z.string().datetime().optional(),
}).refine(
  (data) => {
    if (data.discountType === 'PERCENTAGE' && data.discountValue > 100) {
      return false;
    }
    return true;
  },
  {
    message: 'Percentage discount cannot exceed 100%',
    path: ['discountValue'],
  },
);
export type SetFoodItemDiscountRequest = z.infer<
  typeof SetFoodItemDiscountSchema
>;

export const ToggleAvailabilitySchema = z.object({
  isAvailable: z.boolean(),
});
export type ToggleAvailabilityRequest = z.infer<
  typeof ToggleAvailabilitySchema
>;

export const ReorderFoodItemsSchema = z.object({
  itemIds: z.array(z.string().uuid()),
});
export type ReorderFoodItemsRequest = z.infer<typeof ReorderFoodItemsSchema>;

// ============================================
// PUBLIC MENU SCHEMAS (Customer)
// ============================================

/**
 * Public food item (customer view)
 *
 * Returns items with calculated discount prices, stock levels,
 * and auto-applied badges like "Bestseller" and "Low Stock"
 */
export const PublicFoodItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullish(),
  image: z.string().url().nullish(),
  originalPrice: z.number(),
  effectivePrice: z.number(), // After discount calculation
  currency: z.string(),
  isDiscountActive: z.boolean(),
  discountPercentage: z.number(), // For display purposes
  isVegetarian: z.boolean(),
  isVegan: z.boolean(),
  allergens: z.array(z.string()),
  spiceLevel: z.number().int().min(0).max(3),
  preparationTime: z.number().nullish(),
  remainingStock: z.number().nullish(), // null = unlimited
  isLowStock: z.boolean(), // Auto-applied when remainingStock <= 5
  isBestseller: z.boolean(), // Auto-applied when totalOrders >= 50
  categoryId: z.string().uuid(),
});
export type PublicFoodItem = z.infer<typeof PublicFoodItemSchema>;

export const PublicFoodCategorySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  displayOrder: z.number(),
});
export type PublicFoodCategory = z.infer<typeof PublicFoodCategorySchema>;

export const PublicMenuResponseSchema = z.object({
  menu: z.array(
    z.object({
      category: PublicFoodCategorySchema,
      items: z.array(PublicFoodItemSchema),
    }),
  ),
});
export type PublicMenuResponse = z.infer<typeof PublicMenuResponseSchema>;

// ============================================
// BOOKING FOOD ITEM SCHEMAS
// ============================================

/**
 * Add food item to booking
 *
 * Used when customers select food during booking flow
 */
export const AddFoodToBookingSchema = z.object({
  foodItemId: z.string().uuid(),
  quantity: z.number().int().min(1, 'Quantity must be at least 1'),
});
export type AddFoodToBookingRequest = z.infer<typeof AddFoodToBookingSchema>;

export const BookingFoodItemSchema = z.object({
  id: z.string().uuid(),
  bookingId: z.string().uuid(),
  foodItemId: z.string().uuid(),
  quantity: z.number().int().min(1),
  unitPrice: z.number(),
  totalPrice: z.number(),
  isFreeReward: z.boolean(), // True if from tiered reward
});
export type BookingFoodItem = z.infer<typeof BookingFoodItemSchema>;
